import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { getLoggerSafe } from '@vitvio/logging';
import { createEnvironment, createWebSocketClient } from './relay-environment';
import { RelayEnvironmentProvider } from 'react-relay';
import { healthCheckUrl } from '@config/constants';

const HEALTH_CHECK_INTERVAL_MS = 5000;

interface ConnectionStatusContextType {
  isConnected: boolean | undefined;
  isChecking: boolean;
}

const ConnectionStatusContext = createContext<ConnectionStatusContextType | null>(null);

interface ConnectionStatusProviderProps {
  children: React.ReactNode;
  token: string | undefined;
  serverUrl: string;
  webSocketUrl: string;
}

function ConnectionStatusProvider({ children, token, serverUrl, webSocketUrl }: ConnectionStatusProviderProps) {
  const logger = getLoggerSafe();
  // TODO: we should replace this state with a "Redux-like" store so that we can avoid unnecessary re-renders of the whole application
  const [isHealthy, setIsHealthy] = useState(true);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState<boolean | undefined>(undefined);
  // TODO: technically isChecking should not be set to true on mount, but we plan to simplify this with a state manager
  const [isChecking, setIsChecking] = useState(true);
  const isConnected = isHealthy && isWebSocketConnected !== false;

  useEffect(() => {
    const controller = new AbortController();
    const checkHealth = () =>
      fetch(healthCheckUrl(serverUrl), {
        method: 'GET',
        signal: controller.signal,
        headers: [['Authorization', `Bearer ${token}`]],
      })
        .then((r) => {
          if (!controller.signal.aborted) {
            setIsHealthy(r.ok);
            setIsChecking(false);
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setIsHealthy(false);
          }
        })
        .finally(() => {
          setIsChecking(false);
        });

    checkHealth();

    const interval = setInterval(checkHealth, HEALTH_CHECK_INTERVAL_MS);
    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [serverUrl, token]);

  useEffect(() => {
    if (isWebSocketConnected !== undefined) {
      logger?.debug(`WebSocket ${isWebSocketConnected ? 'connected' : 'disconnected'}`);
    }
  }, [isWebSocketConnected]);

  const wsClient = useMemo(() => {
    const wsClient = createWebSocketClient(webSocketUrl, token);

    wsClient.on('connected', () => setIsWebSocketConnected(true));
    wsClient.on('closed', () => setIsWebSocketConnected(false));
    wsClient.on('error', (error) => {
      logger?.error(error as Error, { context: 'webSocket', webSocketUrl });
      setIsWebSocketConnected(false);
    });

    return wsClient;
  }, [webSocketUrl, token, logger]);

  const environment = useMemo(() => createEnvironment(serverUrl, wsClient, token), [serverUrl, wsClient, token]);

  return (
    <ConnectionStatusContext.Provider
      value={{
        isConnected: isConnected,
        isChecking: isChecking,
      }}
    >
      <RelayEnvironmentProvider environment={environment}>{children}</RelayEnvironmentProvider>
    </ConnectionStatusContext.Provider>
  );
}

function useConnectionStatus() {
  const context = useContext(ConnectionStatusContext);
  if (!context) {
    throw new Error('Connection Status Context was used outside the provider.');
  }
  return context;
}

export { ConnectionStatusProvider, useConnectionStatus };
